import React, { useState, useMemo } from "react";
import { EditDynamicRegimeConfigModal } from "./EditDynamicRegimeConfigModal";
import { ChevronsUpDownIcon } from "lucide-react";

const PRIORITY_COLUMNS = ["underlying", "strategy", "option_type", "symbol"];

const DynamicRegimeConfigDataTable = ({
  regimeConfigData,
  displayName,
  handleEditLotConfig,
}) => {
  const cachedAccess = localStorage.getItem("user_access");
  const userAccess = cachedAccess ? JSON.parse(cachedAccess) : null;
  const isRegimeGridEditAllowed = userAccess?.is_regime_grid_edit === true;

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });

  const orderedColumns = useMemo(() => {
    if (!regimeConfigData || regimeConfigData.length === 0) return [];
    const allKeys = Object.keys(regimeConfigData[0]);
    const priority = PRIORITY_COLUMNS.filter((col) => allKeys.includes(col));
    const remaining = allKeys.filter(
      (col) =>
        !PRIORITY_COLUMNS.includes(col) &&
        !["id", "created_at", "updated_at"].includes(col)
    );

    // Natural alphanumeric sorting for regime codes (e.g. 00, 01, 10, 11 or r000... s000... f000...)
    remaining.sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );

    return [...priority, ...remaining];
  }, [regimeConfigData]);

  const sortedData = useMemo(() => {
    if (!regimeConfigData || regimeConfigData.length === 0) return [];
    if (!sortConfig.key || !sortConfig.direction) {
      return regimeConfigData;
    }

    const sortableData = [...regimeConfigData];

    sortableData.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === "asc" ? -1 : 1;
      if (bValue == null) return sortConfig.direction === "asc" ? 1 : -1;

      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);

      if (
        !isNaN(aNum) &&
        !isNaN(bNum) &&
        typeof aValue === "number" &&
        typeof bValue === "number"
      ) {
        if (sortConfig.direction === "asc") {
          return aNum - bNum;
        }
        return bNum - aNum;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortConfig.direction === "asc") {
        if (aStr < bStr) return -1;
        if (aStr > bStr) return 1;
        return 0;
      } else {
        if (aStr > bStr) return -1;
        if (aStr < bStr) return 1;
        return 0;
      }
    });

    return sortableData;
  }, [regimeConfigData, sortConfig]);

  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (sortConfig.direction === "desc") {
        direction = null;
        setSortConfig({ key: null, direction: null });
        return;
      }
    }

    setSortConfig({ key, direction });
  };

  const getSortIcon = () => {
    return (
      <span className="inline-block ml-1 text-white">
        <ChevronsUpDownIcon className="h-3 w-3 text-white" />
      </span>
    );
  };

  const generateRows = (dataArray) => {
    return dataArray.map((item, index) => (
      <tr
        key={index}
        className="bg-black z-0 border-b last-of-type:border-b-0 border-gray-900 font-medium text-white hover:bg-black/90 text-center"
      >
        {orderedColumns.map((key, idx) => {
          const value = item[key];
          const isNumericNonZero = typeof value === "number" && value > 0;
          return (
            <td
              key={idx}
              className={`px-4 py-2 border-r last-of-type:border-r-0 border-gray-900 ${
                key === "underlying" ||
                key === "strategy" ||
                key === "option_type" ||
                key === "symbol"
                  ? "text-left font-semibold whitespace-nowrap"
                  : ""
              } ${isNumericNonZero ? "bg-emerald-500 font-bold" : ""}`}
            >
              {value !== null && value !== undefined ? String(value) : ""}
            </td>
          );
        })}
        {isRegimeGridEditAllowed && (
          <td
            key="actions"
            className="text-center px-4 py-2 border-r last-of-type:border-r-0 border-gray-900"
          >
            <EditDynamicRegimeConfigModal
              rowData={item}
              displayName={displayName}
              handleEditLotConfig={handleEditLotConfig}
            />
          </td>
        )}
      </tr>
    ));
  };

  const generateTables = (dataArray) => {
    if (!dataArray || dataArray.length === 0) {
      return (
        <div className="flex items-center justify-center text-gray-300 text-md font-medium p-8">
          No Data Available
        </div>
      );
    }

    return (
      <div className="z-0 w-full h-[calc(100vh-140px)] overflow-auto custom-scroll animate-reveal transition-all duration-500 ease-in">
        <table className="bg-black text-sm w-full relative z-0 overflow-x-auto">
          <thead className="bg-[#0F0F0F] sticky top-0 h-[40px] text-sm text-left">
            <tr className="text-white border-b border-gray-800">
              {orderedColumns.map((header, idx) => (
                <th
                  key={idx}
                  className={`border-r last-of-type:border-r-0 border-gray-800 px-3 py-3 font-semibold cursor-pointer hover:bg-gray-800 transition-colors duration-200 select-none ${
                    header === "underlying" ||
                    header === "strategy" ||
                    header === "option_type" ||
                    header === "symbol"
                      ? "text-left"
                      : "text-center"
                  }`}
                  onClick={() => handleSort(header)}
                >
                  <div className="flex items-center justify-between">
                    <span>{header}</span>
                    {getSortIcon()}
                  </div>
                </th>
              ))}
              {isRegimeGridEditAllowed && (
                <th
                  key="actions-header"
                  className="border-r text-center last-of-type:border-r-0 border-gray-800 px-4 py-3 font-semibold"
                >
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody>{generateRows(dataArray)}</tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 overflow-y-auto w-full">
      {generateTables(sortedData)}
    </div>
  );
};

export default DynamicRegimeConfigDataTable;
